/**
 * Контакт: поиск по номеру, карточка с историей, отказ от звонков (§5.3, §5.4).
 *
 * Карточка — рабочий инструмент разбора претензии «почему мне звонят»: в ней
 * видно, откуда номер пришёл и сколько раз был куплен (деньги), кто по нему
 * работал и с какой меткой, и что показала станция по каждому звонку.
 *
 * Телефон отдаётся целиком: маскировать его здесь нечего — именно по нему
 * человек и обратился. Маскирование живёт в логах (§9.4).
 */
import { z } from 'zod';
import { assignmentStatusSchema, bigIdSchema, businessDateSchema, callDispositionSchema, intakeDecisionSchema, isoDateTimeSchema, pageResponseSchema, paginationQuerySchema, phoneE164Schema, queueStatusSchema, refIdSchema, } from './common.dto.js';
import { moneySchema } from './sources.dto.js';
/**
 * GET /api/contacts?phone=… — поиск ТОЛЬКО по номеру.
 *
 * Поиска по имени и по подстроке номера нет намеренно: он не попадает
 * в уникальный индекс и на миллионах контактов кладёт пул API. Номер
 * нормализуется на сервере той же функцией, что и приём, поэтому «8 999…»
 * находит «+7999…».
 */
export const contactSearchQuerySchema = paginationQuerySchema.extend({
    phone: z.string().min(3).max(32),
});
export const contactSearchItemSchema = z.object({
    id: bigIdSchema,
    phone: phoneE164Schema,
    fullName: z.string().nullable(),
    regionCode: z.string().nullable(),
    doNotCall: z.boolean(),
    /** Сколько раз номер становился лидом: повторная покупка «уже отработанного» */
    leadCount: z.number().int().min(0),
    lastSeenAt: isoDateTimeSchema.nullable(),
});
export const contactSearchResponseSchema = pageResponseSchema(contactSearchItemSchema);
/**
 * Факт покупки контакта. Пишется ВСЕГДА, включая отбитые дедупом дубли:
 * за дубль тоже заплачено, и в карточке он обязан быть виден (§5.3).
 */
export const contactDeliverySchema = z.object({
    id: bigIdSchema,
    receivedAt: isoDateTimeSchema,
    businessDate: businessDateSchema,
    sourceId: refIdSchema,
    sourceTitle: z.string(),
    cabinetTitle: z.string(),
    /** Идентификатор заявки на стороне кабинета */
    vid: z.string().nullable(),
    /** Снимок цены на момент приёма — по нему считался расход (§1.5) */
    cost: moneySchema,
    decision: intakeDecisionSchema,
    batchId: bigIdSchema,
});
/** Круг работы диспетчера по контакту и чем он закончился */
export const contactAssignmentSchema = z.object({
    id: bigIdSchema,
    dispatcherId: bigIdSchema,
    dispatcherName: z.string(),
    sourceId: refIdSchema,
    roundNumber: z.number().int().min(1).max(20),
    status: assignmentStatusSchema,
    businessDate: businessDateSchema,
    assignedAt: isoDateTimeSchema,
    labeledAt: isoDateTimeSchema.nullable(),
    labelId: refIdSchema.nullable(),
    labelCode: z.string().nullable(),
    labelTitle: z.string().nullable(),
    isLead: z.boolean(),
    comment: z.string().nullable(),
    /** Счётчики пишет телефония в applyCallResult, второго счётчика нет (§4.5) */
    callsCnt: z.number().int().min(0),
    talkSec: z.number().int().min(0),
    isReached: z.boolean(),
});
export const contactCallSchema = z.object({
    id: bigIdSchema,
    startedAt: isoDateTimeSchema,
    endedAt: isoDateTimeSchema.nullable(),
    dispatcherId: bigIdSchema.nullable(),
    dispatcherName: z.string().nullable(),
    assignmentId: bigIdSchema.nullable(),
    ringSec: z.number().int().min(0),
    talkSec: z.number().int().min(0),
    disposition: callDispositionSchema,
    isReached: z.boolean(),
    /** С какого нашего номера звонили: по нему разбирают выгорание АОНа */
    callerIdUsed: z.string().nullable(),
    /** Запись готова к прослушиванию. Путь к файлу наружу не отдаётся (§7.6) */
    hasRecording: z.boolean(),
});
/** Живой элемент очереди: контакт прямо сейчас в прозвоне */
export const contactQueueSchema = z.object({
    queueItemId: bigIdSchema,
    status: queueStatusSchema,
    roundNumber: z.number().int().min(1).max(20),
    availableFrom: isoDateTimeSchema,
    ownerDispatcherId: bigIdSchema.nullable(),
});
/** GET /api/contacts/:id */
export const contactCardSchema = z.object({
    id: bigIdSchema,
    phone: phoneE164Schema,
    fullName: z.string().nullable(),
    regionCode: z.string().nullable(),
    /** Произвольные поля кабинета закупки: показываются как есть */
    extra: z.record(z.string(), z.unknown()),
    doNotCall: z.boolean(),
    dncReason: z.string().nullable(),
    leadCount: z.number().int().min(0),
    firstSeenAt: isoDateTimeSchema.nullable(),
    lastSeenAt: isoDateTimeSchema.nullable(),
    lastClosedAt: isoDateTimeSchema.nullable(),
    queue: contactQueueSchema.nullable(),
    deliveries: z.array(contactDeliverySchema),
    assignments: z.array(contactAssignmentSchema),
    calls: z.array(contactCallSchema),
});
/**
 * POST /api/contacts/:id/dnc — «не звонить».
 *
 * Причина обязательна: через полгода на претензию отвечают именно ей.
 * Снятия флага в контракте нет — человек отказался от звонков, а не «пока
 * не хочет».
 */
export const setDoNotCallBodySchema = z.object({
    reason: z.string().min(3).max(300),
});
export const setDoNotCallResponseSchema = z.object({
    contactId: bigIdSchema,
    doNotCall: z.literal(true),
    /**
     * Что реально изменилось. Одного флага мало: номер, уже лежащий на пульте,
     * иначе будет обзвонен — поэтому назначение снимается, а элемент очереди
     * уходит в 'excluded' (§6.8). Повтор операции даёт нули, но не ошибку.
     */
    excludedQueueItems: z.number().int().min(0),
    revokedAssignments: z.number().int().min(0),
});
//# sourceMappingURL=contacts.dto.js.map